import React, { forwardRef } from 'react';
import _ from 'lodash';

import useDispensary from 'src/dispensary/hooks/use-dispensary';
import useTranslation from 'hooks/use-translation';
import useUI from 'hooks/use-ui';

import { tracker } from 'src/utils/analytics';
import { ProductRecommendationSource } from 'src/utils/analytics/trackers/internal-gtm-tracker/internal-gtm-tracker.types';

import EmptyLoadingState from 'components/empty-loading-state';
import CarouselSlider from 'components/carousel-slider';
import { CarouselProductCard } from 'src/components/product-card/carousel-product-card';
import { useAddToCartModal, findSingleWeightItem } from 'components/modals/add-to-cart';
import { applySettings } from '../carousels.utils';
import { dimensions, CardCarouselItem, Section } from '../carousels.styles';
import { CarouselHeader } from '../carousel-header';

const generateSlides = ({ dispensary, products, sectionIndex, UI, handleAddToCart }) => {
  const setTrackerContext = (index) => {
    tracker.setContext({
      activeProductPosition: {
        fromCarousel: true,
        index,
        section: UI.carouselLabels[sectionIndex]?.name,
        source: ProductRecommendationSource.native,
      },
    });
  };
  return _.map(applySettings(products), (product, index) => {
    const position = {
      productId: product.id,
      productIndex: index,
      sectionIndex,
    };
    return (
      <CardCarouselItem key={product?.id} role='group' aria-roledescription='slide' aria-label={product?.name}>
        <CarouselProductCard
          dimensions={dimensions}
          dispensary={dispensary}
          product={product}
          productIndex={index}
          sectionIndex={sectionIndex}
          width='100%'
          onQuickAddClick={() => {
            UI.setProductPosition(position);
            setTrackerContext(index);
            handleAddToCart(product);
          }}
          onClick={() => {
            UI.setProductPosition(position);
            setTrackerContext(index);
          }}
        />
      </CardCarouselItem>
    );
  });
};

export const GenericCarousel = forwardRef((props, ref) => {
  const UI = useUI();
  const { open: openAddToCartModal } = useAddToCartModal();

  const {
    showLink = true,
    hasBeenVisible,
    isLoading,
    label,
    linkLabel,
    products,
    query,
    route,
    href,
    section,
    sectionIndex,
  } = props;
  const showTitle = section?.hideTitle !== `true`;
  const newSectionPositions = UI?.carouselLabels ?? [];
  newSectionPositions.splice(sectionIndex, 1, { name: label });
  UI?.setCarouselLabels(newSectionPositions);
  const { t } = useTranslation();
  const { dispensary } = useDispensary();
  const labelId = `${section.id}-label`;

  // provide accessible label info
  let carouselProps = {};

  if (showTitle) {
    carouselProps = {
      'aria-labelledby': labelId,
    };
  } else {
    carouselProps = {
      'aria-label': label,
    };
  }

  const handleAddToCart = async (product) => {
    openAddToCartModal({
      ...findSingleWeightItem(product),
      product,
      dispensary,
    });
  };

  const slides = generateSlides({ dispensary, products, sectionIndex, UI, handleAddToCart });

  return (
    // refs are stupid and get gobbled up in styled-components
    // we also need some default height here so that we get a sense of
    // where the visible components should lay
    <div ref={ref} style={{ minHeight: '420px' }}>
      {hasBeenVisible && (
        // eslint-disable-next-line i18next/no-literal-string
        <EmptyLoadingState isLoading={isLoading} page='productCarousel'>
          <Section>
            {(showLink || showTitle) &&
              CarouselHeader({
                href,
                isEmbeddedCarousel: UI.isEmbeddedCarousel,
                label,
                labelId,
                linkLabel,
                query,
                route,
                showLink,
                showTitle,
                t,
              })}
            <CarouselSlider carouselProps={carouselProps}>{slides}</CarouselSlider>
          </Section>
        </EmptyLoadingState>
      )}
    </div>
  );
});
